import React, { useEffect } from 'react';
import { Receipt } from 'lucide-react';
import { Card, CardHeader, CardFooter, AddButton, CollapsibleContent, EditModeToggle, EditModeCheckbox, EditModeActionBar } from '../shared';
import { InlineEditField } from '../shared/InlineEditField';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { useEditMode } from '../../hooks/useEditMode';
import { formatCurrency } from '../../utils/formatters';
import { Liability } from '../../types';

interface LiabilitiesWidgetProps {
  onAdd: () => void;
  variant?: 'card' | 'page';
}

export const LiabilitiesWidget: React.FC<LiabilitiesWidgetProps> = ({ onAdd, variant = 'card' }) => {
  const { darkMode, privacyMode } = useTheme();
  const { sheetId } = useAuth();
  const { liabilities, updateLiability, totalLiabilities, handleDeleteLiability, handleBatchDelete } = useFinancialData();
  const { editValue, setEditValue, isEditing, startEdit, cancelEdit, completeEdit } = useInlineEdit();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('liabilities');

  const handleDeleteAsync = async (index: number) => {
    if (!sheetId) return;
    await handleDeleteLiability(index, sheetId);
  };

  const handleBatchDeleteAsync = async (indices: number[]) => {
    if (!sheetId) return;
    await handleBatchDelete('Liabilities', indices, sheetId);
  };

  const editMode = useEditMode(handleDeleteAsync, liabilities.length, handleBatchDeleteAsync);
  const { exitEditMode } = editMode;

  // Exit edit mode when widget collapses
  useEffect(() => {
    if (collapsed) exitEditMode();
  }, [collapsed, exitEditMode]);

  const handleSave = async (index: number, field: 'value' | 'interestRate') => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await updateLiability(index, field, value, sheetId);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, onSave: () => void) => {
    if (e.key === 'Enter') onSave();
    if (e.key === 'Escape') cancelEdit();
  };

  const isPage = variant === 'page';

  return (
    <Card variant={variant}>
      <CardHeader
        title="Liabilities"
        icon={<Receipt size={20} />}
        action={
          <div className="flex items-center gap-2">
            <EditModeToggle
              isEditMode={editMode.isEditMode}
              onToggle={editMode.toggleEditMode}
              itemCount={liabilities.length}
            />
            <AddButton onClick={onAdd} />
          </div>
        }
        collapsed={isPage ? false : collapsed}
        onToggleCollapse={isPage ? undefined : () => toggleCollapse('liabilities')}
        variant={variant}
      />

      <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
        {editMode.isEditMode && (
          <EditModeActionBar
            selectedCount={editMode.selectedIndices.size}
            totalCount={liabilities.length}
            allSelected={editMode.selectedIndices.size === liabilities.length}
            onSelectAll={() => editMode.selectAll(liabilities.length)}
            onDeselectAll={editMode.deselectAll}
            onDeleteSelected={editMode.deleteSelected}
            isDeleting={editMode.isDeleting}
          />
        )}

        {liabilities.length === 0 ? (
          <div className={`text-center py-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            <p className="text-sm">No liabilities yet</p>
            <p className="text-xs mt-1">Add liabilities to track debts</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} border-b ${darkMode ? 'border-white/10' : 'border-slate-200'}`}>
                  {editMode.isEditMode && <th className="w-8 pb-2" />}
                  <th className="text-left pb-2">Liability</th>
                  <th className="text-right pb-2 w-[30%]">Amount</th>
                  <th className="text-right pb-2 w-20">APR %</th>
                </tr>
              </thead>
              <tbody>
                {liabilities.map((liability: Liability, idx: number) => (
                  <tr
                    key={idx}
                    className={`${darkMode ? 'text-slate-300' : 'text-slate-800'} border-b ${darkMode ? 'border-white/10' : 'border-slate-100'}`}
                  >
                    {editMode.isEditMode && (
                      <td className="py-2 pr-2">
                        <EditModeCheckbox
                          checked={editMode.selectedIndices.has(idx)}
                          onToggle={() => editMode.toggleIndex(idx)}
                        />
                      </td>
                    )}
                    <td className="py-2 font-semibold">{liability.name}</td>
                    <td className="text-right">
                      <div className="inline-block">
                        <InlineEditField
                          value={liability.value}
                          isEditing={isEditing('liability', idx, 'value')}
                          editValue={editValue}
                          onEditValueChange={setEditValue}
                          onStartEdit={() => startEdit('liability', idx, 'value', liability.value)}
                          onSave={() => handleSave(idx, 'value')}
                          onCancel={cancelEdit}
                          editable={liability.editable}
                          variant="negative"
                          privacyMode={privacyMode}
                        />
                      </div>
                    </td>
                    <td className="text-right w-20">
                      {isEditing('liability', idx, 'interestRate') ? (
                        <input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, () => handleSave(idx, 'interestRate'))}
                          onBlur={() => handleSave(idx, 'interestRate')}
                          onFocus={e => e.target.select()}
                          className={`w-16 px-2 py-0.5 text-xs rounded border ${
                            darkMode
                              ? 'bg-slate-800 border-slate-600 text-white'
                              : 'bg-white border-slate-200 text-slate-800'
                          } focus:outline-none focus:ring-1 focus:ring-purple-500 text-right`}
                          autoFocus
                          step="0.01"
                        />
                      ) : (
                        <span
                          onClick={() => startEdit('liability', idx, 'interestRate', liability.interestRate)}
                          className={`cursor-pointer hover:bg-white/10 rounded px-1 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}
                        >
                          {privacyMode ? '••••' : `${liability.interestRate.toFixed(2)}%`}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </CollapsibleContent>

      <CardFooter>
        <div className="flex justify-between items-center font-semibold text-sm">
          <span className={`${darkMode ? 'text-white' : 'text-slate-800'} flex-1`}>
            Total
          </span>
          <span className={`${darkMode ? 'text-red-400' : 'text-red-600'} text-right`}>
            {formatCurrency(totalLiabilities, privacyMode)}
          </span>
          <span className="w-20" />
        </div>
      </CardFooter>
    </Card>
  );
};
